/**
 * The county case an appeal opens (29 Sep 2026), shown under the case chart.
 *
 * Three states, one line each:
 *  - this IS a county case, a link back to the case it was appealed from;
 *  - a county case has been opened from this one, a link to it;
 *  - the case has an appeal bond and no county case yet, the button that opens one.
 *
 * On any other case it draws nothing: most cases never appeal, and a disabled button on every one
 * of them would be clutter.
 *
 * Opening sends nothing to the court or the client, and no LOR: the appeal bond puts the firm on
 * record (Lloyd, 29 Sep 2026). The dialog says so, because "open a case" reads like the start of the
 * normal intake emails, and it is deliberately not.
 *
 * It also settles the appeal fee, which must be paid or marked included before the case can open.
 * Marking it included is a person's confirmation, never automatic, Lloyd's words.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import CourtSelect from '../CourtSelect';
import { FullCourtDto } from '../../api/CourtApi';

export interface AppealFeeState {
  paid: boolean;
  includedAt: string | null;
  includedBy: string | null;
}

export interface LinkedCase {
  guid: string;
  matterNumber: string | null;
  courtName?: string | null;
}

interface CountyCasePanelProps {
  ticketGuid: string;
  countyCase: LinkedCase | null;
  appealedFrom: LinkedCase | null;
  /** An appeal bond exists on this case, the only time the button is offered. */
  hasAppealBond: boolean;
  appealFee: AppealFeeState | null;
  /** Called after the county case is opened, so the page can re-read the chart. */
  onOpened: () => void;
  /** Called after the appeal fee mark changes, so the page re-reads what the dialog shows. */
  onAppealFeeChanged: () => Promise<void> | void;
}

function apiHost(): string {
  return process.env.API_HOST?.trim() || window.location.origin;
}

const caseHref = (guid: string) => `/ticket?Ticket_guid=${guid}`;

const CountyCasePanel: React.FC<CountyCasePanelProps> = ({
  ticketGuid,
  countyCase,
  appealedFrom,
  hasAppealBond,
  appealFee,
  onOpened,
  onAppealFeeChanged,
}) => {
  const [open, setOpen] = React.useState(false);
  const [court, setCourt] = React.useState<FullCourtDto | null>(null);
  const [causeNumber, setCauseNumber] = React.useState('');
  const [blockers, setBlockers] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [markingFee, setMarkingFee] = React.useState(false);
  // Bumped after the fee mark changes, so the readiness check runs again.
  const [recheck, setRecheck] = React.useState(0);

  // Re-asked whenever the court changes, so "same court as the original" shows before Save.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const query = court ? `?courtGuid=${encodeURIComponent(court.Court_guid)}` : '';
    fetch(`${apiHost()}/api/v1/manager/ticket/${ticketGuid}/county-case/readiness${query}`, {
      credentials: 'include',
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (!cancelled && body) setBlockers(body.blockers ?? []);
      })
      .catch(() => {
        // The POST checks again and reports; a failed pre-check only loses the early warning.
      });
    return () => {
      cancelled = true;
    };
  }, [open, court, ticketGuid, recheck]);

  const setIncluded = async (included: boolean) => {
    setMarkingFee(true);
    setError(null);
    try {
      const response = await fetch(
        `${apiHost()}/api/v1/manager/ticket/${ticketGuid}/appeal-fee-included`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ included }),
        },
      );
      if (!response.ok) {
        setError('The appeal fee could not be updated.');
        return;
      }
      await onAppealFeeChanged();
      setRecheck((n) => n + 1);
    } catch {
      setError('The appeal fee could not be updated.');
    } finally {
      setMarkingFee(false);
    }
  };

  const close = () => {
    setOpen(false);
    setCourt(null);
    setCauseNumber('');
    setBlockers([]);
    setError(null);
  };

  const submit = async () => {
    if (!court) return;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${apiHost()}/api/v1/manager/ticket/${ticketGuid}/county-case`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          courtGuid: court.Court_guid,
          causeNumber: causeNumber.trim() || null,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (Array.isArray(body.blockers)) setBlockers(body.blockers);
        setError(body.message || 'The county case was not opened.');
        return;
      }
      close();
      onOpened();
    } catch {
      setError('The county case was not opened.');
    } finally {
      setSaving(false);
    }
  };

  if (appealedFrom) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        County case, opened on appeal from{' '}
        <Link component={RouterLink} to={caseHref(appealedFrom.guid)}>
          {appealedFrom.matterNumber ?? 'the original case'}
        </Link>
        .
      </Typography>
    );
  }

  if (countyCase) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Appealed to {countyCase.courtName ?? 'county'}:{' '}
        <Link component={RouterLink} to={caseHref(countyCase.guid)}>
          {countyCase.matterNumber ?? 'open the county case'}
        </Link>
        . The county row above reads that case.
      </Typography>
    );
  }

  if (!hasAppealBond) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
        Open county case
      </Button>

      <Dialog open={open} onClose={saving ? undefined : close} fullWidth maxWidth="sm">
        <DialogTitle>Open the county case</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A new case for this client at the county court, linked to this one. This case stays
            open and keeps its own court, cause number and prosecutor. No letter of representation
            is sent, because the appeal bond puts us on record, and nothing is sent to the client.
          </Typography>

          <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Appeal fee
            </Typography>
            {appealFee?.paid ? (
              <Typography variant="body2">Paid — a payment marked as the appeal fee is on this case.</Typography>
            ) : appealFee?.includedAt ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  Included in the original fee — confirmed
                  {appealFee.includedBy ? ` by ${appealFee.includedBy}` : ''} on{' '}
                  {appealFee.includedAt.slice(0, 10)}.
                </Typography>
                <Button size="small" disabled={markingFee || saving} onClick={() => setIncluded(false)}>
                  Undo
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Not settled. Either send the client an appeal fee payment link from the Payments
                  tab, or confirm the appeal is included in what they already paid.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={markingFee || saving}
                  onClick={() => setIncluded(true)}
                >
                  {markingFee ? 'Saving…' : 'Confirm: included in the original fee'}
                </Button>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CourtSelect
              label="County court appealed to"
              value={court}
              onChange={setCourt}
              required
              disabled={saving}
            />
            <TextField
              label="County cause number (if known)"
              value={causeNumber}
              onChange={(event) => setCauseNumber(event.target.value)}
              disabled={saving}
              size="small"
              inputProps={{ maxLength: 100 }}
            />
          </Box>

          {blockers.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {blockers.map((blocker) => (
                <div key={blocker}>{blocker}</div>
              ))}
            </Alert>
          )}
          {error && blockers.length === 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving || !court || blockers.length > 0}
          >
            {saving ? 'Opening…' : 'Open county case'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CountyCasePanel;
